import { jsPDF } from "jspdf";
import {
  SOURCES, WEALTH_FORECASTS, sourceById, RISK_REGISTER, METHODOLOGY, ACCESS_DATE,
} from "./indexData";

const INK = [26, 29, 36];
const GOLD = [176, 141, 87];
const GRAY = [110, 123, 139];
const M = 56; // margin

function newDoc() {
  return new jsPDF({ unit: "pt", format: "a4" });
}

function footer(doc, page) {
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();
  doc.setDrawColor(220, 216, 208);
  doc.line(M, h - 44, w - M, h - 44);
  doc.setFont("times", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...GRAY);
  doc.text("EverSoul Legacy Intelligence Index 2026", M, h - 30);
  doc.text(String(page), w - M, h - 30, { align: "right" });
}

// Simple flowing text writer that paginates.
function makeWriter(doc) {
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();
  const maxW = w - M * 2;
  let y = M + 20;
  let page = 1;
  footer(doc, page);

  const ensure = (need) => {
    if (y + need > h - 70) {
      doc.addPage();
      page += 1;
      y = M + 20;
      footer(doc, page);
    }
  };
  return {
    get page() { return page; },
    heading(t, size = 18) {
      ensure(size + 24);
      doc.setFont("times", "bold");
      doc.setFontSize(size);
      doc.setTextColor(...INK);
      const lines = doc.splitTextToSize(t, maxW);
      doc.text(lines, M, y);
      y += lines.length * (size + 4) + 6;
      doc.setDrawColor(...GOLD);
      doc.setLineWidth(1.2);
      doc.line(M, y, M + 46, y);
      doc.setLineWidth(0.5);
      y += 16;
    },
    label(t) {
      ensure(20);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.setTextColor(...GOLD);
      doc.text(t.toUpperCase(), M, y);
      y += 16;
    },
    body(t, size = 10.5) {
      doc.setFont("times", "normal");
      doc.setFontSize(size);
      doc.setTextColor(...INK);
      const lines = doc.splitTextToSize(t, maxW);
      lines.forEach((ln) => {
        ensure(size + 4);
        doc.text(ln, M, y);
        y += size + 4;
      });
      y += 8;
    },
    bullets(items) {
      doc.setFont("times", "normal");
      doc.setFontSize(10.5);
      doc.setTextColor(...INK);
      items.forEach((it) => {
        const lines = doc.splitTextToSize(it, maxW - 16);
        ensure(lines.length * 14 + 2);
        doc.setTextColor(...GOLD);
        doc.text("—", M, y);
        doc.setTextColor(...INK);
        doc.text(lines, M + 16, y);
        y += lines.length * 14 + 2;
      });
      y += 8;
    },
    space(n = 10) { y += n; },
    pageBreak() { doc.addPage(); page += 1; y = M + 20; footer(doc, page); },
  };
}

export function generateExecutiveBriefing() {
  const doc = newDoc();
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();

  // Cover
  doc.setFillColor(247, 245, 240);
  doc.rect(0, 0, w, h, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...GOLD);
  doc.text("EVERSOUL · RESEARCH", M, 130);
  doc.setFont("times", "bold");
  doc.setFontSize(30);
  doc.setTextColor(...INK);
  doc.text(doc.splitTextToSize("EverSoul Legacy Intelligence Index 2026", w - M * 2), M, 190);
  doc.setFont("times", "italic");
  doc.setFontSize(13);
  doc.setTextColor(...GRAY);
  doc.text(doc.splitTextToSize("Artificial Intelligence, Inheritance, and the Emerging Governance of Personal Identity", w - M * 2), M, 250);
  doc.setDrawColor(...GOLD);
  doc.line(M, 300, M + 60, 300);
  doc.setFont("times", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...INK);
  doc.text("The financial inheritance system is mature.", M, 340);
  doc.text("The personal AI inheritance system barely exists.", M, 356);
  doc.setFontSize(9);
  doc.setTextColor(...GRAY);
  doc.text(`Research & informational report · Data accessed ${ACCESS_DATE}`, M, h - 90);
  doc.text("Not legal, financial, tax, fiduciary, investment, or estate-planning advice.", M, h - 76);

  const wr = makeWriter(doc.addPage ? (doc.addPage(), doc) : doc);
  // makeWriter added footer on the fresh page above; write content:

  wr.label("Executive thesis");
  wr.heading("The next great inheritance challenge is identity itself.");
  wr.body("Artificial intelligence is moving from generating content to representing individuals — through voice, likeness, memory, values, communication style, and increasingly persistent personal context. At the same time, trillions of dollars in wealth, control, responsibility, and family stewardship are moving between generations.");
  wr.body("The institutional question is no longer only what happens to a person's assets. It is also what happens to the intelligent representation of that person. Legacy AI is becoming technically inevitable. Governance is not.");

  wr.label("Why this matters now");
  wr.heading("Capability is advancing faster than governance.", 15);
  wr.body("As AI becomes capable of preserving voice, likeness, memory, values, judgment, and personal history, institutions will need new systems for consent, access, fidelity, fiduciary control, inheritance conditions, and permanent retirement. The risk emerges when technological capability advances faster than the legal, fiduciary, and operational systems intended to control it.");

  wr.label("Global wealth transfer");
  wr.heading("An historic transfer of wealth and responsibility is underway.", 15);
  wr.body("The significance is not that EverSoul captures a share of transferred assets. It is that the banks, trusts, family offices, attorneys, and advisors overseeing this transfer will increasingly encounter digital identity, personal AI, likeness, voice, memory, and posthumous-access questions. Published estimates (kept separate by methodology and geography):");
  wr.bullets(WEALTH_FORECASTS.map((f) => {
    const s = sourceById(f.sourceId);
    return `${f.total} — ${f.geography}, ${f.forecastPeriod} (${s.org}, ${s.year}). ${f.note}`;
  }));

  wr.label("Expansion of inheritable assets");
  wr.heading("What society considers inheritable is expanding.", 15);
  wr.body("Estate planning has developed systems for transferring nearly every major form of value — from physical property and financial assets to businesses, intellectual property, digital archives, and digital assets. It has not yet developed mature systems for inheriting an interactive representation of the individual: a persistent, governed personal AI.");

  wr.label("The personal AI governance gap");
  wr.heading("The gap between capability and readiness is widening.", 15);
  wr.body("EverSoul's analytical indices score AI capability and institutional governance readiness on a 0–100 scale. Capability rises steeply; readiness lags across legal clarity, consent standards, trust-document coverage, fiduciary authority, authentication, access controls, audit infrastructure, modification restrictions, retirement procedures, and vendor continuity. These are EverSoul analytical indices, not externally published market indices.");

  wr.label("Institutional risk map");
  wr.heading("Risk scales with sophistication and consequence.", 15);
  wr.body("As a personal AI moves from passive archive to interactive family history to advisory guidance to fiduciary significance, formal governance becomes required. The more an AI representation can influence future decisions, the less appropriate it becomes to treat it as ordinary digital content.");

  wr.label("Selected risk register (excerpt)");
  wr.bullets(RISK_REGISTER.slice(0, 8).map((r) => `${r.name} — likelihood ${r.likelihood}, severity ${r.severity}. Control: ${r.control}.`));

  wr.label("EverSoul governance architecture");
  wr.heading("Governance infrastructure for personal AI continuity.", 15);
  wr.body("EverSoul is designed as a layered governance system: human intent defines what may be preserved; legal and fiduciary authority is retained by trustees, executors, and attorneys; EverSoul enforces consent, permissions, identity fidelity, release conditions, auditing, and retirement; the personal AI provides only approved content; and beneficiaries interact within defined permissions. The AI is never the legal authority.");

  wr.label("Institutional readiness");
  wr.heading("Is your organization prepared?", 15);
  wr.body("A twelve-question assessment scores organizations from Unaddressed to Recognized to Partially Governed to Institutionally Prepared across ownership, consent, differentiated access, suspension rights, modification restrictions, auditability, authentication, correction, vendor continuity, retirement, and the primacy of human legal authority.");

  wr.label("Partnership opportunities");
  wr.bullets([
    "Private banks & trust companies — client retention and defensible governance of interactive identity.",
    "Estate attorneys — testamentary authority, consent, enforceability, and model retirement.",
    "Family offices — founder intent, succession, confidentiality, and stewardship.",
    "Insurers — fraud, impersonation, and identity-authentication exposure.",
    "Technology companies — model integrity, portability, and system retirement.",
  ]);

  wr.label("Methodology");
  wr.body(METHODOLOGY.summary);
  wr.body(METHODOLOGY.scoring);
  wr.body(METHODOLOGY.confidence + " " + METHODOLOGY.limitations);

  wr.pageBreak();
  wr.label("Sources & disclaimers");
  wr.heading("Sources", 15);
  wr.bullets(SOURCES.map((s) => `${s.org}. ${s.title} (${s.year}). ${s.classification.toUpperCase()}. ${s.url}`));
  wr.label("Disclaimers");
  wr.body("This index is provided for research and informational purposes and does not constitute legal, financial, tax, fiduciary, investment, or estate-planning advice. Forecasts and scenarios are inherently uncertain, and EverSoul modeled scenarios are not predictions of guaranteed outcomes. Third-party data remains subject to the methodologies and limitations of its original sources. Institutional, legal, and regulatory developments may differ by jurisdiction. Personal AI must remain subordinate to valid legal instruments and authorized human fiduciaries.");

  doc.save("EverSoul-Legacy-Intelligence-Index-2026.pdf");
}

export function generateReadinessReport(result) {
  const doc = newDoc();
  const wr = makeWriter(doc);
  wr.label("EverSoul · Institutional readiness");
  wr.heading("Personal AI Inheritance — Readiness Report");
  wr.body(`Assessment date: ${new Date().toLocaleDateString()}`);
  wr.space(4);
  wr.label("Readiness level");
  wr.heading(`${result.level}  ·  ${result.score} / 12`, 16);
  wr.body(result.note);
  if (result.risks?.length) {
    wr.label("Primary risks");
    wr.bullets(result.risks);
  }
  if (result.missing?.length) {
    wr.label("Missing controls");
    wr.bullets(result.missing);
  }
  wr.label("Recommended next steps");
  wr.bullets(result.steps);
  wr.space(6);
  wr.label("Disclaimer");
  wr.body("This assessment is informational and does not constitute legal, financial, fiduciary, or estate-planning advice.");
  doc.save("EverSoul-Readiness-Report.pdf");
}
